import React, { useState } from "react";
import { auditForms, AuditForm } from "../data/auditForms";
import { auditApi } from "../services/api";

interface AuditItemRecord {
  status: string;
  value: string;
  note: string;
  evidence: string[];
}

export interface AuditRecord {
  id: string;
  formId: string;
  formName: string;
  period: string;
  completedAt: string;
  completedBy: string;
  items: Record<string, AuditItemRecord>;
  score: number;
  failed: number;
}

const STORE_KEY = "audit_form_records";

function loadHistory(): AuditRecord[] {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "[]");
  } catch (error) {
    return [];
  }
}

function emptyItems(form: AuditForm): Record<string, AuditItemRecord> {
  const items: Record<string, AuditItemRecord> = {};

  form.sections.forEach(section =>
    section.items.forEach(item => {
      items[item.id] = { status: "", value: "", note: "", evidence: [] };
    })
  );

  return items;
}

export default function Audits({ user }: { user?: any }) {
  const [formId, setFormId] = useState("af-daily");
  const [items, setItems] = useState<Record<string, AuditItemRecord>>(() => emptyItems(auditForms[0]));
  const [history, setHistory] = useState<AuditRecord[]>(loadHistory);
  const [showHistory, setShowHistory] = useState(false);
  const [message, setMessage] = useState("");

  const form = auditForms.find(f => f.id === formId) || auditForms[0];

  const switchForm = (id: string) => {
    const next = auditForms.find(f => f.id === id);

    if (!next) return;

    setFormId(id);
    setItems(emptyItems(next));
    setMessage("");
  };

  const updateItem = (itemId: string, patch: Partial<AuditItemRecord>) => {
    setItems(current => ({ ...current, [itemId]: { ...current[itemId], ...patch } }));
  };

  const attachEvidence = async (itemId: string, file: File) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("kind", file.type.startsWith("video") ? "video" : "photo");
      formData.append("context", `audit:${formId}:${itemId}`);
      await auditApi.uploadEvidence(formData);
      updateItem(itemId, { evidence: [...(items[itemId]?.evidence || []), `${file.name} (uploaded)`] });
      setMessage("Evidence uploaded to server.");
    } catch (error) {
      updateItem(itemId, { evidence: [...(items[itemId]?.evidence || []), `${file.name} (saved locally)`] });
      setMessage("Upload not available, evidence saved with the record.");
    }
  };

  const completeAudit = () => {
    let pass = 0;
    let failed = 0;
    let total = 0;

    form.sections.forEach(section =>
      section.items.forEach(item => {
        const rec = items[item.id];

        if (!rec) return;

        total += 1;
        if (rec.status === "pass") pass += 1;
        if (rec.status === "fail") failed += 1;
      })
    );

    const score = total === 0 ? 0 : Math.round((pass / total) * 100);

    const record: AuditRecord = {
      id: `${form.id}-${Date.now()}`,
      formId: form.id,
      formName: form.name,
      period: form.period,
      completedAt: new Date().toISOString(),
      completedBy: user && user.name ? user.name : "Unknown",
      items,
      score,
      failed,
    };

    const nextHistory = [record, ...history];

    setHistory(nextHistory);
    localStorage.setItem(STORE_KEY, JSON.stringify(nextHistory));
    setMessage(`${form.name} saved. Score ${score}% with ${failed} failed items.`);
    setItems(emptyItems(form));
  };
  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Audits</h2>
          <p className="text-sm text-slate-600">{form.description}</p>
        </div>
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="rounded-lg border border-blue-900 px-4 py-2 text-sm font-medium text-blue-900"
        >
          {showHistory ? "Back to audit" : `History (${history.length})`}
        </button>
      </div>

      {showHistory ? (
        <div className="space-y-3">
          {history.map(record => (
            <div key={record.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-medium text-slate-900">{record.formName}</div>
                <div className="text-sm text-slate-600">Score {record.score}% | Failed {record.failed}</div>
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {new Date(record.completedAt).toLocaleString()} by {record.completedBy}
              </div>
            </div>
          ))}
          {history.length === 0 ? <div className="text-sm text-slate-500">No audits recorded yet.</div> : null}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {auditForms.map(f => (
              <button
                key={f.id}
                onClick={() => switchForm(f.id)}
                className={
                  f.id === formId
                    ? "rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white"
                    : "rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"
                }
              >
                {f.name}
              </button>
            ))}
          </div>

          {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

          {form.sections.map(section => (
            <div key={section.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="mb-3 font-semibold text-slate-900">{section.title}</h3>
              <div className="space-y-4">
                {section.items.map(item => {
                  const rec = items[item.id] || { status: "", value: "", note: "", evidence: [] };

                  return (
                    <div key={item.id} className="rounded-lg border border-slate-100 p-3">
                      <div className="text-sm font-medium text-slate-800">{item.label}</div>
                      {item.hint ? <div className="text-xs text-slate-500">{item.hint}</div> : null}

                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <button onClick={() => updateItem(item.id, { status: "pass" })} className={rec.status === "pass" ? "rounded-lg bg-green-700 px-3 py-1 text-sm text-white" : "rounded-lg border border-slate-300 px-3 py-1 text-sm text-slate-700"}>Pass</button>
                        <button onClick={() => updateItem(item.id, { status: "fail" })} className={rec.status === "fail" ? "rounded-lg bg-red-700 px-3 py-1 text-sm text-white" : "rounded-lg border border-slate-300 px-3 py-1 text-sm text-slate-700"}>Fail</button>
                        <button onClick={() => updateItem(item.id, { status: "na" })} className={rec.status === "na" ? "rounded-lg bg-slate-600 px-3 py-1 text-sm text-white" : "rounded-lg border border-slate-300 px-3 py-1 text-sm text-slate-700"}>N/A</button>

                        {item.type === "reading" ? (
                          <input value={rec.value} onChange={event => updateItem(item.id, { value: event.target.value })} placeholder="Value" className="w-24 rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                        ) : null}

                        <label className="cursor-pointer rounded-lg border border-slate-300 px-3 py-1 text-sm text-slate-700">
                          Add photo
                          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={event => { const file = event.target.files && event.target.files[0]; if (file) attachEvidence(item.id, file); }} />
                        </label>
                        <label className="cursor-pointer rounded-lg border border-slate-300 px-3 py-1 text-sm text-slate-700">
                          Add video
                          <input type="file" accept="video/*" className="hidden" onChange={event => { const file = event.target.files && event.target.files[0]; if (file) attachEvidence(item.id, file); }} />
                        </label>
                      </div>

                      <input value={rec.note} onChange={event => updateItem(item.id, { note: event.target.value })} placeholder="Note" className="mt-2 w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                      {rec.evidence.length > 0 ? <div className="mt-1 text-xs text-slate-500">{rec.evidence.join(", ")}</div> : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <button onClick={completeAudit} className="rounded-lg bg-blue-900 px-5 py-2 text-sm font-medium text-white">
            Complete and save audit
          </button>
        </>
      )}
    </div>
  );
}